import groupBy from 'lodash/groupBy'
import omit from 'lodash/omit'
import sessionStorage from '../../common/session-storage'
import {
  ALERT_ERROR,
  CREATE_ANNOTATION_ERROR,
  CREATE_ANNOTATION_PENDING,
  CREATE_ANNOTATION_SUCCESS,
  CREATE_ANNOTATION_FORBIDDEN,
  DELETE_ANNOTATION_SUCCESS,
  DESELECT_ANNOTATION,
  DELETE_ROOT_COMMENT_SUCCESS,
  GENERATE_MERGED_PDF_DONE,
  GENERATE_MERGED_PDF_ERROR,
  GENERATE_MERGED_PDF_PENDING,
  GET_ANNOTATIONS_ERROR,
  GET_ANNOTATIONS_PENDING,
  GET_ANNOTATIONS_SUCCESSFUL,
  HOVER_ANNOTATION,
  MERGED_PDF_TIMED_OUT,
  SELECT_ANNOTATION,
  STAGE_ANNOTATION,
  UPDATE_ANNOTATION_ERROR,
  UPDATE_ANNOTATION_PENDING,
  UPDATE_ANNOTATION_SUCCESS,
} from './constants'

const EMPTY_ARRAY = []

const initialState = {
  annotationForbidden: false,
  data: EMPTY_ARRAY,
  stagedByPage: {},
  isLoading: false,
  selectedAnnotationId: null,
  hoveredAnnotationId: null,
  wasLoadSuccessful: false,
  error: null,
  isEditingAnnotation: false,
  mergedPdfStatus: 'None',
  annotationError: false,
}

export default function annotations(state = initialState, action) {
  switch (action.type) {
    case SELECT_ANNOTATION:
      return {
        ...state,
        selectedAnnotationId: action.annotationId,
      }

    case DESELECT_ANNOTATION:
      const selectedAnnotationId =
        state.selectedAnnotationId === action.annotationId ? null : state.selectedAnnotationId
      return {
        ...state,
        selectedAnnotationId,
      }

    case HOVER_ANNOTATION:
      return {
        ...state,
        hoveredAnnotationId: action.annotationId,
      }

    case GET_ANNOTATIONS_PENDING:
      return {
        ...state,
        isLoading: true,
        wasLoadSuccessful: false,
        error: null,
      }

    case GET_ANNOTATIONS_SUCCESSFUL: {
      let annotations = action.response.data
      const sessionId = action.sessionId
      const sessionAnnotations = sessionStorage.annotations
      if (sessionAnnotations && sessionId) {
        const parsedAnnotations = JSON.parse(sessionAnnotations)
        const sessionParsedAnnotations = parsedAnnotations[sessionId]
        if (sessionParsedAnnotations && sessionParsedAnnotations.length > 0) {
          annotations = [...annotations, ...sessionParsedAnnotations]
        }
      }
      const uniqueAnnotations = annotations.filter((annotation, index) => {
        if (!annotation?.rect || annotation?.type === 'commentReply') return true

        return (
          annotations.findIndex(
            (ann) =>
              ann?.id === annotation?.id || ann?.rect?.toString() === annotation?.rect?.toString(),
          ) === index
        )
      })
      const newState = {
        ...state,
        data: uniqueAnnotations,
        stagedByPage: groupBy(uniqueAnnotations, (annotation) => annotation.page),
        isLoading: false,
        wasLoadSuccessful: true,
        error: null,
      }
      const lastVerifiedState = omit(newState, ['lastVerifiedState'])
      return {
        ...newState,
        lastVerifiedState,
      }
    }

    case GET_ANNOTATIONS_ERROR:
      return {
        ...state,
        isLoading: false,
        wasLoadSuccessful: false,
        error: action.error,
      }

    case DELETE_ANNOTATION_SUCCESS: {
      const filteredPageAnnotations = (state.stagedByPage[action.annotationPage] || []).filter(
        (annotation) => annotation.id !== action.annotationId,
      )

      const pageHasNonCommentAnnotations = filteredPageAnnotations.some(
        (annotation) => annotation.type !== 'commentReply',
      )

      const stagedByPage = Object.assign({}, state.stagedByPage, {
        [action.annotationPage]: pageHasNonCommentAnnotations ? filteredPageAnnotations : [],
      })

      return {
        ...state,
        stagedByPage,
        data: state.data.filter((annotation) => annotation.id !== action.annotationId),
        selectedAnnotationId:
          state.selectedAnnotationId === action.annotationId ? null : state.selectedAnnotationId,
        hoveredAnnotationId: null,
      }
    }

    case DELETE_ROOT_COMMENT_SUCCESS: {
      const stagedByPage = Object.assign({}, state.stagedByPage, {
        [action.annotationPage]: (state.stagedByPage[action.annotationPage] || []).filter(
          (annotation) => annotation.inreplyto !== action.baseAnnotationId,
        ),
      })

      return {
        ...state,
        stagedByPage,
        data: state.data.filter((annotation) => annotation.inreplyto !== action.baseAnnotationId),
        selectedAnnotationId:
          state.selectedAnnotationId === action.annotationId ? null : state.selectedAnnotationId,
        hoveredAnnotationId: null,
      }
    }

    case CREATE_ANNOTATION_PENDING: {
      const index = (state.stagedByPage[action.annotation.page] || []).findIndex(
        (annotation) => annotation.id === action.annotation.id,
      )
      const annotationsOnPage = state.stagedByPage[action.annotation.page] || []
      const stagedByPage = Object.assign({}, state.stagedByPage, {
        [action.annotation.page]: annotationsOnPage
          .slice(0, index === -1 ? annotationsOnPage.length : index)
          .concat(action.annotation)
          .concat(index === -1 ? [] : annotationsOnPage.slice(index + 1)),
      })
      const returnState =
        action.annotation.type !== 'freetext'
          ? {
              ...state,
              stagedByPage,
              hoveredAnnotationId: null,
              selectedAnnotationId: action.annotation.inreplyto || action.annotation.id,
            }
          : {
              ...state,
              stagedByPage,
              hoveredAnnotationId: null,
            }
      return returnState
    }

    case UPDATE_ANNOTATION_PENDING: {
      const index = (state.stagedByPage[action.annotation.page] || []).findIndex(
        (annotation) => annotation.id === action.annotation.id,
      )
      const annotationsOnPage = state.stagedByPage[action.annotation.page] || []
      const stagedByPage = Object.assign({}, state.stagedByPage, {
        [action.annotation.page]: annotationsOnPage
          .slice(0, index === -1 ? annotationsOnPage.length : index)
          .concat(action.annotation)
          .concat(index === -1 ? [] : annotationsOnPage.slice(index + 1)),
      })
      return {
        ...state,
        stagedByPage,
      }
    }

    case CREATE_ANNOTATION_SUCCESS: {
      const annotationsOnPage = state.stagedByPage[action.annotation.page] || []
      const stagedByPage = Object.assign({}, state.stagedByPage, {
        [action.annotation.page]: annotationsOnPage
          .filter((annotation) => annotation.id !== action.annotation.id)
          .concat(action.annotation),
      })
      const newState = {
        ...state,
        data: state.data.concat(action.annotation),
        stagedByPage,
      }
      const lastVerifiedState = omit(newState, ['lastVerifiedState'])
      return {
        ...newState,
        lastVerifiedState,
      }
    }

    case CREATE_ANNOTATION_ERROR: {
      const annotationsOnPage = state.stagedByPage[action.annotation.page] || []
      const stagedByPage = Object.assign({}, state.stagedByPage, {
        [action.annotation.page]: annotationsOnPage.filter(
          (annotation) => annotation.id !== action.annotation.id,
        ),
      })
      return {
        ...state,
        stagedByPage,
        data: state.data.filter((annotation) => annotation.id !== action.annotation.id),
        selectedAnnotationId:
          state.selectedAnnotationId === action.annotation.id ? null : state.selectedAnnotationId,
        hoveredAnnotationId: null,
      }
    }

    case CREATE_ANNOTATION_FORBIDDEN: {
      return {
        ...state,
        annotationForbidden: action.show,
      }
    }

    case UPDATE_ANNOTATION_SUCCESS: {
      const newState = {
        ...state,
        data: state.data
          .filter((annotation) => annotation.id !== action.annotation.id)
          .concat(action.annotation),
      }
      const lastVerifiedState = omit(newState, ['lastVerifiedState'])
      return {
        ...newState,
        lastVerifiedState,
      }
    }

    case UPDATE_ANNOTATION_ERROR:
      return {
        ...state.lastVerifiedState,
        lastVerifiedState: state.lastVerifiedState,
      }

    case STAGE_ANNOTATION: {
      const index = (state.stagedByPage[action.annotation.page] || []).findIndex(
        (annotation) => annotation.id === action.annotation.id,
      )
      const annotationsOnPage = state.stagedByPage[action.annotation.page] || []
      const stagedByPage = Object.assign({}, state.stagedByPage, {
        [action.annotation.page]: annotationsOnPage
          .slice(0, index === -1 ? annotationsOnPage.length : index)
          .concat(action.annotation)
          .concat(index === -1 ? [] : annotationsOnPage.slice(index + 1)),
      })
      return {
        ...state,
        selectedAnnotationId: action.annotation.id,
        stagedByPage,
      }
    }

    case GENERATE_MERGED_PDF_PENDING:
      return {
        ...state,
        mergedPdfStatus: 'Waiting',
      }

    case GENERATE_MERGED_PDF_ERROR:
    case MERGED_PDF_TIMED_OUT:
      return {
        ...state,
        mergedPdfStatus: 'Error',
        error: action.error,
      }

    case GENERATE_MERGED_PDF_DONE:
      return {
        ...state,
        mergedPdfStatus: 'Done',
      }

    case ALERT_ERROR:
      return {
        ...state,
        alertErrorMessage: action.message,
        annotationError: action.show,
      }

    default:
      return state
  }
}
